/* eslint-disable react-hooks/set-state-in-effect -- Modal uses guarded setState calls in effects to reset state on open and to apply preselection. */
import { Check, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  PlaceholderMemberService,
  formatCurrency,
  queryKeys,
} from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { GroupWithDashboard } from '@splitfiesta/shared';
import { PAYMENT_METHODS, getCurrencyDecimals } from '@splitfiesta/shared';
import { Avatar, Badge, Button, Card } from '@splitfiesta/ui';

import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { PlaceholderBadge } from '@/components/members/PlaceholderBadge';
import CurrencyInput from '@/components/ui/CurrencyInput';
import { Portal } from '@/components/ui/Portal';

import { useDebtSuggestionsQuery } from '@/hooks/queries/useBalances';
import { useRecordPaymentMutation } from '@/hooks/queries/useSettlements';
import { useToast } from '@/hooks/useToast';

import { resolveUserName } from '@/lib/displayName';

import { useAuthStore } from '@/stores/authStore';

import { celebrate } from '@/utils/confetti';

interface CreditRecommendation {
  fromUser: {
    id: string;
    name: string;
    profileImage?: string;
  };
  toUser: {
    id: string;
    name: string;
    profileImage?: string;
  };
  amount: number;
  currency: string;
  priority?: number;
}

interface RecordPaymentFormData {
  groupId: string;
  fromUserId?: string;
  currency: string;
  amount?: number;
  method?: string;
  notes?: string;
}

interface RecordPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: GroupWithDashboard;
  /** Pre-select a debtor and skip to step 2 (useful when triggered from a specific suggestion) */
  preselectedDebtorId?: string;
  /** When true, the group owner is recording a payment between two other members (auto-completes immediately) */
  ownerMode?: boolean;
  /** The creditor ID when in owner mode (the person receiving payment) */
  preselectedCreditorId?: string;
  /** When true, owner can select ANY debtor/creditor pair from balances (not just from suggestions) */
  flexibleOwnerMode?: boolean;
}

type Step = 0 | 1 | 2 | 3;

export function RecordPaymentModal({
  isOpen,
  onClose,
  group,
  preselectedDebtorId,
  ownerMode = false,
  preselectedCreditorId,
  flexibleOwnerMode = false,
}: RecordPaymentModalProps) {
  const { t } = useTranslation();

  // Load balances namespace for flexible mode translations
  useNamespace('balances');
  const { toast } = useToast();
  const user = useAuthStore(state => state.user);
  const queryClient = useQueryClient();

  // In flexible mode, start at step 0 (creditor selection); otherwise step 1
  const [currentStep, setCurrentStep] = useState<Step>(
    flexibleOwnerMode ? 0 : 1
  );
  const [formData, setFormData] = useState<RecordPaymentFormData>({
    groupId: group.id,
    currency: group.currency,
    method: 'cash',
  });

  const [amountString, setAmountString] = useState<string | undefined>('');

  const [errors, setErrors] = useState<
    Partial<Record<keyof RecordPaymentFormData, string>>
  >({});

  // For flexible owner mode: track selected creditor
  const [selectedCreditorId, setSelectedCreditorId] = useState<
    string | undefined
  >();

  // Track if submission is in progress (stays true until modal closes)
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track if we've handled preselection for this modal session
  const hasHandledPreselection = useRef(false);
  // Guard against auto-submit during initialization
  const isInitializing = useRef(false);
  // Track if we've reset submitting state for this modal session
  const hasResetSubmitting = useRef(false);

  const recordPaymentMutation = useRecordPaymentMutation();

  // Fetch simplified debt suggestions WITHOUT filtering by user
  // We want ALL debts to find those where current user is the creditor (toUser)
  const { data: debtSuggestions, isLoading: isLoadingDebts } =
    useDebtSuggestionsQuery(
      group.id,
      isOpen,
      false // Don't filter by user - get all debts
    );

  // Get recommendations from the API response
  // Handle both data structures: direct {recommendations: [...]} or wrapped {data: {recommendations: [...]}}
  const allDebts = useMemo((): CreditRecommendation[] => {
    if (!debtSuggestions) return [];

    // Check if recommendations is at top level (unwrapped response from useDebtSuggestionsQuery)
    if ('recommendations' in debtSuggestions) {
      return (debtSuggestions.recommendations as CreditRecommendation[]) || [];
    }

    // Check if data is wrapped (cached response from PaymentSuggestions query)
    if ('data' in debtSuggestions && debtSuggestions.data) {
      const data = debtSuggestions.data as {
        recommendations?: CreditRecommendation[];
      };
      if ('recommendations' in data) {
        return data.recommendations || [];
      }
    }

    return [];
  }, [debtSuggestions]);

  // The simplified-debt recommendations carry no placeholder flag, so the
  // group balances stay the source of truth for it in the debt-based list.
  const placeholderIds = useMemo(
    () => PlaceholderMemberService.placeholderIdSet(group.balances ?? []),
    [group.balances]
  );

  // Filter to members who owe the current user (or preselected creditor in owner mode) money
  const membersWhoOweMe = useMemo(() => {
    // In owner mode with preselected creditor, show debts owed to that creditor
    const creditorId =
      ownerMode && preselectedCreditorId ? preselectedCreditorId : user?.id;
    if (!creditorId) return [];

    // Filter debts where the creditor is the toUser
    return allDebts
      .filter(debt => debt.toUser.id === creditorId)
      .map(debt => ({
        userId: debt.fromUser.id,
        userName: debt.fromUser.name,
        profileImage: debt.fromUser.profileImage,
        amountOwed: debt.amount,
        currency: debt.currency,
        isPlaceholder: placeholderIds.has(debt.fromUser.id),
      }));
  }, [allDebts, user, ownerMode, preselectedCreditorId, placeholderIds]);

  // Create a map of credit amounts for easy lookup (keyed by debtor/fromUser ID)
  // In owner mode, use the preselected creditor instead of current user
  const targetCreditorId =
    ownerMode && preselectedCreditorId ? preselectedCreditorId : user?.id;
  const creditMap = new Map<string, CreditRecommendation>(
    allDebts
      .filter(debt => debt.toUser.id === targetCreditorId)
      .map(debt => [debt.fromUser.id, debt as CreditRecommendation])
  );

  // Get creditor info for display in owner mode
  const creditorInfo = useMemo(() => {
    if (!ownerMode || !preselectedCreditorId) return null;

    // Try to find creditor from allDebts
    const debtWithCreditor = allDebts.find(
      debt => debt.toUser.id === preselectedCreditorId
    );
    if (debtWithCreditor) {
      return {
        id: debtWithCreditor.toUser.id,
        name: debtWithCreditor.toUser.name,
        profileImage: debtWithCreditor.toUser.profileImage,
      };
    }

    // Fallback to group balances (which contains member info)
    const memberFromBalance = group.balances.find(
      b => b.userId === preselectedCreditorId
    );
    if (memberFromBalance) {
      return {
        id: memberFromBalance.userId,
        name: memberFromBalance.userName,
        profileImage: memberFromBalance.profileImage,
      };
    }

    return null;
  }, [ownerMode, preselectedCreditorId, allDebts, group.balances]);

  // ===== FLEXIBLE OWNER MODE: Balance-based member lists =====

  // Members who are OWED money (positive balance) - potential creditors for flexible mode
  const membersOwedMoney = useMemo(() => {
    if (!flexibleOwnerMode) return [];
    return group.balances
      .filter(b => b.balance > 0.01) // They are owed money
      .map(b => ({
        userId: b.userId,
        userName: b.userName,
        profileImage: b.profileImage,
        balance: b.balance,
        currency: b.currency,
        isPlaceholder: Boolean(b.isPlaceholder),
      }))
      .sort((a, b) => b.balance - a.balance); // Sort by amount owed (highest first)
  }, [flexibleOwnerMode, group.balances]);

  // Members who OWE money (negative balance) - potential debtors for flexible mode
  const membersWhoOweMoney = useMemo(() => {
    if (!flexibleOwnerMode) return [];
    return group.balances
      .filter(b => b.balance < -0.01) // They owe money
      .map(b => ({
        userId: b.userId,
        userName: b.userName,
        profileImage: b.profileImage,
        amountOwed: Math.abs(b.balance),
        currency: b.currency,
        isPlaceholder: Boolean(b.isPlaceholder),
      }))
      .sort((a, b) => b.amountOwed - a.amountOwed); // Sort by amount owed (highest first)
  }, [flexibleOwnerMode, group.balances]);

  // Get the selected creditor info for flexible mode
  const flexibleCreditorInfo = useMemo(() => {
    if (!flexibleOwnerMode || !selectedCreditorId) return null;
    return membersOwedMoney.find(m => m.userId === selectedCreditorId) || null;
  }, [flexibleOwnerMode, selectedCreditorId, membersOwedMoney]);

  // Calculate max settlement amount for flexible mode
  const maxFlexibleAmount = useMemo(() => {
    if (!flexibleOwnerMode || !selectedCreditorId || !formData.fromUserId) {
      return Infinity;
    }
    const creditor = membersOwedMoney.find(
      m => m.userId === selectedCreditorId
    );
    const debtor = membersWhoOweMoney.find(
      m => m.userId === formData.fromUserId
    );
    if (!creditor || !debtor) return 0;
    return Math.min(creditor.balance, debtor.amountOwed);
  }, [
    flexibleOwnerMode,
    selectedCreditorId,
    formData.fromUserId,
    membersOwedMoney,
    membersWhoOweMoney,
  ]);

  // Reset refs when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasHandledPreselection.current = false;
      isInitializing.current = false;
      hasResetSubmitting.current = false;
    }
  }, [isOpen]);

  // Reset form when modal opens (only once per session)
  useEffect(() => {
    if (!isOpen) return;

    // Reset submitting state when modal opens (for fresh start) - only once per session
    if (!hasResetSubmitting.current) {
      hasResetSubmitting.current = true;

      setIsSubmitting(false);
    }

    // Already handled this modal session
    if (hasHandledPreselection.current) return;

    // ALWAYS wait for debts to finish loading before making any decisions
    if (isLoadingDebts) {
      return;
    }

    // Mark as initializing to prevent auto-submit
    isInitializing.current = true;
    hasHandledPreselection.current = true;

    // Check if we have a preselected debtor
    if (preselectedDebtorId && allDebts.length > 0) {
      // In owner mode, find debt to preselected creditor; otherwise to current user
      const creditorToMatch =
        ownerMode && preselectedCreditorId ? preselectedCreditorId : user?.id;
      const credit = allDebts.find(
        debt =>
          debt.fromUser.id === preselectedDebtorId &&
          debt.toUser.id === creditorToMatch
      );
      if (credit) {
        // Pre-select the debtor and start at step 2
        const decimals = getCurrencyDecimals(group.currency);

        setFormData({
          groupId: group.id,
          currency: group.currency,
          method: 'cash',
          fromUserId: preselectedDebtorId,
          amount: credit.amount,
        });
        setAmountString(credit.amount.toFixed(decimals));
        setCurrentStep(2);
        setErrors({});

        // Clear initializing flag after a delay
        setTimeout(() => {
          isInitializing.current = false;
        }, 500);
        return;
      }
    }

    // Default reset (no preselection)
    // In flexible mode, start at step 0; otherwise step 1
    setCurrentStep(flexibleOwnerMode ? 0 : 1);
    setFormData({
      groupId: group.id,
      currency: group.currency,
      method: 'cash',
    });
    setAmountString('');
    setErrors({});
    // Reset flexible mode state
    if (flexibleOwnerMode) {
      setSelectedCreditorId(undefined);
    }

    // Clear initializing flag
    setTimeout(() => {
      isInitializing.current = false;
    }, 100);

    // Celebrate if no one owes the user anything (only in non-flexible mode)
    if (!flexibleOwnerMode && membersWhoOweMe.length === 0) {
      setTimeout(() => {
        celebrate('subtle');
      }, 400);
    }
  }, [
    isOpen,
    isLoadingDebts,
    group.id,
    group.currency,
    membersWhoOweMe.length,
    preselectedDebtorId,
    allDebts,
    user?.id,
    ownerMode,
    preselectedCreditorId,
    flexibleOwnerMode,
  ]);

  const validateStep = useCallback(
    (step: Step): boolean => {
      const newErrors: Partial<Record<keyof RecordPaymentFormData, string>> =
        {};

      if (step === 0) {
        // Flexible mode step 0: must select a creditor
        if (flexibleOwnerMode && !selectedCreditorId) {
          // No specific error field for creditor, validation handled by UI
          return false;
        }
      } else if (step === 1) {
        if (!formData.fromUserId) {
          newErrors.fromUserId = t('settlements:errors.selectDebtor');
        }
      } else if (step === 2) {
        if (!formData.amount || formData.amount <= 0) {
          newErrors.amount = t('settlements:errors.invalidAmount');
        }
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [
      formData.fromUserId,
      formData.amount,
      t,
      flexibleOwnerMode,
      selectedCreditorId,
    ]
  );

  const handleNext = useCallback(() => {
    if (validateStep(currentStep)) {
      setCurrentStep((currentStep + 1) as Step);
    }
  }, [currentStep, validateStep]);

  const handleBack = useCallback(() => {
    setCurrentStep((currentStep - 1) as Step);
    setErrors({});
  }, [currentStep]);

  const handleSubmit = useCallback(async () => {
    if (!validateStep(2)) {
      return;
    }

    if (!formData.fromUserId || !formData.amount) {
      return;
    }

    // In owner mode, we also need the creditor ID
    // In flexible mode, use selectedCreditorId; in regular owner mode, use preselectedCreditorId
    const creditorId = flexibleOwnerMode
      ? selectedCreditorId
      : preselectedCreditorId;
    if ((ownerMode || flexibleOwnerMode) && !creditorId) {
      return;
    }

    // Set submitting state to keep button disabled until modal closes
    setIsSubmitting(true);

    try {
      await recordPaymentMutation.mutateAsync({
        groupId: formData.groupId,
        fromUserId: formData.fromUserId,
        amount: formData.amount,
        currency: formData.currency,
        method: formData.method,
        notes: formData.notes,
        // Include owner mode fields when applicable (both regular ownerMode and flexibleOwnerMode)
        ...((ownerMode || flexibleOwnerMode) &&
          creditorId && {
            initiatedByOwner: true,
            toUserId: creditorId,
          }),
      });

      // Await refetches BEFORE closing modal to ensure fresh data is displayed
      // This is needed because mutateAsync resolves before onSuccess completes
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: queryKeys.simplifiedDebts(formData.groupId, true),
        }),
        queryClient.refetchQueries({
          queryKey: queryKeys.simplifiedDebts(formData.groupId, false),
        }),
        queryClient.refetchQueries({
          queryKey: queryKeys.groupWithDashboard(formData.groupId),
        }),
      ]);

      // Celebrate with confetti when payment is recorded
      celebrate('medium');

      toast({
        title: t('settlements:messages.paymentRecorded'),
        type: 'success',
      });
      onClose();
    } catch (_error) {
      setIsSubmitting(false);
      toast({
        title: t('common:errors.genericError'),
        description: t('settlements:errors.recordPaymentFailed'),
        type: 'error',
      });
    }
  }, [
    validateStep,
    recordPaymentMutation,
    formData,
    toast,
    t,
    onClose,
    queryClient,
    ownerMode,
    preselectedCreditorId,
    flexibleOwnerMode,
    selectedCreditorId,
  ]);

  // Handle Enter key for step 2 and 3 (disabled during initialization)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Block ALL Enter key handling during initialization to prevent auto-submit
      if (isInitializing.current) {
        return;
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();

        if (currentStep === 2) {
          if (formData.amount && formData.amount > 0) {
            handleNext();
          }
        } else if (currentStep === 3) {
          if (!isSubmitting) {
            handleSubmit();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    isOpen,
    currentStep,
    formData.amount,
    isSubmitting,
    handleNext,
    handleSubmit,
  ]);

  const selectedCredit = creditMap.get(formData.fromUserId || '');
  const selectedMember = membersWhoOweMe.find(
    m => m.userId === formData.fromUserId
  );

  const handleDebtorSelect = (userId: string) => {
    const credit = creditMap.get(userId);
    const newAmount = credit ? credit.amount : formData.amount;
    setFormData({
      ...formData,
      fromUserId: userId,
      amount: newAmount,
    });
    // Also update the string representation for CurrencyInput
    if (credit && credit.amount) {
      const decimals = getCurrencyDecimals(group.currency);
      setAmountString(credit.amount.toFixed(decimals));
    }
    // Auto-advance to next step
    setTimeout(() => setCurrentStep(2), 300);
  };

  const handleSuggestedAmount = () => {
    if (selectedCredit) {
      const decimals = getCurrencyDecimals(group.currency);
      setAmountString(selectedCredit.amount.toFixed(decimals));
      setFormData({
        ...formData,
        amount: selectedCredit.amount,
      });
    }
  };

  // ===== FLEXIBLE OWNER MODE: Handlers =====

  // Handle creditor selection in flexible mode (Step 0)
  const handleCreditorSelect = (userId: string) => {
    setSelectedCreditorId(userId);
    // Reset debtor and amount when creditor changes
    setFormData({
      ...formData,
      fromUserId: undefined,
      amount: undefined,
    });
    setAmountString('');
    // Auto-advance to step 1 (debtor selection)
    setTimeout(() => setCurrentStep(1), 300);
  };

  // Handle debtor selection in flexible mode (Step 1)
  const handleFlexibleDebtorSelect = (userId: string) => {
    const debtor = membersWhoOweMoney.find(m => m.userId === userId);
    const creditor = membersOwedMoney.find(
      m => m.userId === selectedCreditorId
    );
    // Calculate max amount as min of debtor's debt and creditor's credit
    const maxAmount =
      debtor && creditor
        ? Math.min(debtor.amountOwed, creditor.balance)
        : debtor?.amountOwed || 0;
    const decimals = getCurrencyDecimals(group.currency);
    setFormData({
      ...formData,
      fromUserId: userId,
      amount: maxAmount,
    });
    setAmountString(maxAmount.toFixed(decimals));
    // Auto-advance to step 2 (amount)
    setTimeout(() => setCurrentStep(2), 300);
  };

  // Get selected member info for display (works for both modes)
  const selectedMemberForDisplay = flexibleOwnerMode
    ? membersWhoOweMoney.find(m => m.userId === formData.fromUserId)
    : selectedMember;

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50"
          onClick={e => {
            // Only close if clicking directly on the backdrop, not bubbled events
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
          data-testid="record-payment-modal-backdrop"
        />

        {/* Modal Content - wrapped in div to stop event propagation */}
        <div
          className="relative z-50"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <Card
            className="w-full max-w-[90vw] sm:w-auto sm:min-w-[500px] sm:max-w-[650px] mx-2 p-0"
            padding="none"
            data-testid="record-payment-modal"
          >
            <div className="relative overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {ownerMode || flexibleOwnerMode
                      ? t('settlements:actions.recordPaymentGeneric')
                      : t('settlements:actions.recordPayment')}
                  </h2>
                  {/* Owner mode indicator */}
                  {(ownerMode || flexibleOwnerMode) && (
                    <Badge variant="warning" size="sm">
                      {t('settlements:labels.recordingAsOwner')}
                    </Badge>
                  )}
                  {/* Step Indicator - 4 steps in flexible mode, 3 steps otherwise */}
                  <div className="flex items-center gap-1.5">
                    {(flexibleOwnerMode ? [0, 1, 2, 3] : [1, 2, 3]).map(
                      step => (
                        <div
                          key={step}
                          className={`h-2 w-2 rounded-full transition-all ${
                            step === currentStep
                              ? 'bg-blue-600 w-6'
                              : step < currentStep
                                ? 'bg-blue-400'
                                : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        />
                      )
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  aria-label={t('common:actions.close')}
                  className="p-2 min-h-[44px] min-w-[44px]"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Step Content */}
              <div className="px-6 py-6">
                {/* Step 0: Select Creditor (who received the payment) - Flexible Mode Only */}
                {currentStep === 0 && flexibleOwnerMode && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        {t('settlements:labels.whoGotPaid')}
                      </h3>
                      {membersOwedMoney.length > 0 && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('settlements:labels.selectCreditorFlexible')}
                        </p>
                      )}
                    </div>

                    {isLoadingDebts ? (
                      <div className="text-center py-12">
                        <LoadingSpinner size="lg" className="mx-auto mb-4" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('common:loading')}
                        </p>
                      </div>
                    ) : membersOwedMoney.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">🎉</div>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t('settlements:placeholders.noOneOwedMoney')}
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Owner notice */}
                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-center mb-4">
                          <p className="text-sm text-amber-700 dark:text-amber-400">
                            {t('settlements:labels.flexibleSettlementNotice')}
                          </p>
                        </div>
                        <div className="space-y-3 max-h-[350px] overflow-y-auto">
                          {membersOwedMoney.map(member => (
                            <button
                              key={member.userId}
                              onClick={() =>
                                handleCreditorSelect(member.userId)
                              }
                              className="w-full p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 transition-all hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md active:scale-[0.98]"
                              data-testid={`creditor-card-${member.userId}`}
                            >
                              {/* Desktop Layout */}
                              <div className="hidden sm:flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <Avatar
                                    name={member.userName}
                                    src={member.profileImage}
                                    size="md"
                                    className="shrink-0"
                                  />
                                  <p className="font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                    {resolveUserName(member.userName, t)}
                                  </p>
                                  {member.isPlaceholder && (
                                    <PlaceholderBadge className="ml-2" />
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-1 shrink-0">
                                  <p className="text-xl font-bold text-green-600 whitespace-nowrap">
                                    {formatCurrency(
                                      member.balance,
                                      member.currency || group.currency
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    {t('balances:isOwed')}
                                  </p>
                                </div>
                                <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                              </div>

                              {/* Mobile Layout */}
                              <div className="flex sm:hidden flex-col gap-3">
                                <div className="flex items-center justify-end gap-3">
                                  <Avatar
                                    name={member.userName}
                                    src={member.profileImage}
                                    size="md"
                                    className="shrink-0"
                                  />
                                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {resolveUserName(member.userName, t)}
                                  </p>
                                  {member.isPlaceholder && (
                                    <PlaceholderBadge className="ml-2" />
                                  )}
                                  <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                                </div>
                                <div className="pl-13 flex flex-col items-end gap-1">
                                  <p className="text-2xl font-bold text-green-600">
                                    {formatCurrency(
                                      member.balance,
                                      member.currency || group.currency
                                    )}
                                  </p>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {t('balances:isOwed')}
                                  </p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Step 1: Select Debtor (who paid) */}
                {currentStep === 1 && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        {ownerMode || flexibleOwnerMode
                          ? t('settlements:labels.whoMadePayment')
                          : t('settlements:labels.whoPaidYou')}
                      </h3>
                      {/* Show description based on mode */}
                      {flexibleOwnerMode
                        ? membersWhoOweMoney.length > 0 && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {t('settlements:labels.selectDebtorOwner', {
                                name: resolveUserName(
                                  flexibleCreditorInfo?.userName,
                                  t
                                ),
                              })}
                            </p>
                          )
                        : membersWhoOweMe.length > 0 && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {ownerMode && creditorInfo
                                ? t('settlements:labels.selectDebtorOwner', {
                                    name: resolveUserName(creditorInfo.name, t),
                                  })
                                : t('settlements:labels.selectDebtor')}
                            </p>
                          )}
                    </div>

                    {isLoadingDebts ? (
                      <div className="text-center py-12">
                        <LoadingSpinner size="lg" className="mx-auto mb-4" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('common:loading')}
                        </p>
                      </div>
                    ) : flexibleOwnerMode ? (
                      /* Flexible Owner Mode: Use balance-based debtor list */
                      membersWhoOweMoney.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="text-6xl mb-4">🎉</div>
                          <p className="text-gray-600 dark:text-gray-400">
                            {t('settlements:placeholders.noDebts')}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[350px] overflow-y-auto">
                          {membersWhoOweMoney.map(member => {
                            // Calculate max for this debtor with selected creditor
                            const creditor = membersOwedMoney.find(
                              m => m.userId === selectedCreditorId
                            );
                            const maxAmount = creditor
                              ? Math.min(member.amountOwed, creditor.balance)
                              : member.amountOwed;
                            return (
                              <button
                                key={member.userId}
                                onClick={() =>
                                  handleFlexibleDebtorSelect(member.userId)
                                }
                                className="w-full p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 transition-all hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md active:scale-[0.98]"
                                data-testid={`debtor-card-${member.userId}`}
                              >
                                {/* Desktop Layout */}
                                <div className="hidden sm:flex items-center justify-between gap-4">
                                  <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <Avatar
                                      name={member.userName}
                                      src={member.profileImage}
                                      size="md"
                                      className="shrink-0"
                                    />
                                    <p className="font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                      {resolveUserName(member.userName, t)}
                                    </p>
                                    {member.isPlaceholder && (
                                      <PlaceholderBadge className="ml-2" />
                                    )}
                                  </div>
                                  <div className="flex flex-col items-end gap-1 shrink-0">
                                    <p className="text-xl font-bold text-blue-600 whitespace-nowrap">
                                      {formatCurrency(
                                        maxAmount,
                                        member.currency || group.currency
                                      )}
                                    </p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      {t(
                                        'settlements:labels.maxSettlementAmount',
                                        {
                                          amount: formatCurrency(
                                            maxAmount,
                                            member.currency || group.currency
                                          ),
                                        }
                                      ).replace(/^[^:]+:\s*/, '')}
                                    </p>
                                  </div>
                                  <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                                </div>

                                {/* Mobile Layout */}
                                <div className="flex sm:hidden flex-col gap-3">
                                  <div className="flex items-center justify-end gap-3">
                                    <Avatar
                                      name={member.userName}
                                      src={member.profileImage}
                                      size="md"
                                      className="shrink-0"
                                    />
                                    <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                      {resolveUserName(member.userName, t)}
                                    </p>
                                    {member.isPlaceholder && (
                                      <PlaceholderBadge className="ml-2" />
                                    )}
                                    <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                                  </div>
                                  <div className="pl-13 flex flex-col items-end gap-1">
                                    <p className="text-2xl font-bold text-blue-600">
                                      {formatCurrency(
                                        maxAmount,
                                        member.currency || group.currency
                                      )}
                                    </p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                      {t('settlements:labels.owes', {
                                        name: resolveUserName(
                                          flexibleCreditorInfo?.userName,
                                          t
                                        ),
                                      })}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )
                    ) : /* Regular mode: Use suggestion-based debtor list */
                    membersWhoOweMe.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">🎉</div>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t('settlements:placeholders.noCredits')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[350px] overflow-y-auto">
                        {membersWhoOweMe.map(member => {
                          const credit = creditMap.get(member.userId);
                          return (
                            <button
                              key={member.userId}
                              onClick={() => handleDebtorSelect(member.userId)}
                              className="w-full p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 transition-all hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md active:scale-[0.98]"
                              data-testid={`debtor-card-${member.userId}`}
                            >
                              {/* Desktop Layout */}
                              <div className="hidden sm:flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <Avatar
                                    name={member.userName}
                                    src={member.profileImage}
                                    size="md"
                                    className="shrink-0"
                                  />
                                  <p className="font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                    {resolveUserName(member.userName, t)}
                                  </p>
                                  {member.isPlaceholder && (
                                    <PlaceholderBadge className="ml-2" />
                                  )}
                                </div>
                                {credit && group.simplifyDebts && (
                                  <div className="flex flex-col items-end gap-1 shrink-0">
                                    <p className="text-xl font-bold text-blue-600 whitespace-nowrap">
                                      {formatCurrency(
                                        credit.amount,
                                        credit.currency || group.currency
                                      )}
                                    </p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      {ownerMode && creditorInfo
                                        ? t('settlements:labels.owes', {
                                            name: resolveUserName(
                                              creditorInfo.name,
                                              t
                                            ),
                                          })
                                        : t('settlements:labels.owesYou')}
                                    </p>
                                  </div>
                                )}
                                <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                              </div>

                              {/* Mobile Layout */}
                              <div className="flex sm:hidden flex-col gap-3">
                                <div className="flex items-center justify-end gap-3">
                                  <Avatar
                                    name={member.userName}
                                    src={member.profileImage}
                                    size="md"
                                    className="shrink-0"
                                  />
                                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {resolveUserName(member.userName, t)}
                                  </p>
                                  {member.isPlaceholder && (
                                    <PlaceholderBadge className="ml-2" />
                                  )}
                                  <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                                </div>
                                {credit && group.simplifyDebts && (
                                  <div className="pl-13 flex flex-col items-end gap-1">
                                    <p className="text-2xl font-bold text-blue-600">
                                      {formatCurrency(
                                        credit.amount,
                                        credit.currency || group.currency
                                      )}
                                    </p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                      {ownerMode && creditorInfo
                                        ? t('settlements:labels.owes', {
                                            name: resolveUserName(
                                              creditorInfo.name,
                                              t
                                            ),
                                          })
                                        : t('settlements:labels.owesYou')}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {errors.fromUserId && (
                      <p className="text-sm text-red-500 text-center mt-2">
                        {errors.fromUserId}
                      </p>
                    )}
                  </div>
                )}

                {/* Step 2: Enter Amount & Method */}
                {currentStep === 2 && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        {t('settlements:fields.amount')}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {flexibleOwnerMode && flexibleCreditorInfo
                          ? t('settlements:labels.howMuchDidTheyPayOwner', {
                              debtor: resolveUserName(
                                selectedMemberForDisplay?.userName,
                                t
                              ),
                              creditor: resolveUserName(
                                flexibleCreditorInfo.userName,
                                t
                              ),
                            })
                          : ownerMode && creditorInfo
                            ? t('settlements:labels.howMuchDidTheyPayOwner', {
                                debtor: resolveUserName(
                                  selectedMemberForDisplay?.userName,
                                  t
                                ),
                                creditor: resolveUserName(creditorInfo.name, t),
                              })
                            : t('settlements:labels.howMuchDidTheyPay', {
                                name: resolveUserName(
                                  selectedMemberForDisplay?.userName,
                                  t
                                ),
                              })}
                      </p>
                    </div>

                    {/* Amount input */}
                    <div className="flex justify-center">
                      <div className="w-full max-w-md">
                        <CurrencyInput
                          id="amount"
                          name="amount"
                          value={amountString}
                          currency={group.currency}
                          placeholder="0.00"
                          onValueChange={(value, _name, values) => {
                            let numericValue = values?.float ?? 0;

                            // Cap amount to maximum - use flexible amount for flexible mode, otherwise credit amount
                            const maxOwed = flexibleOwnerMode
                              ? maxFlexibleAmount
                              : selectedCredit?.amount;
                            if (
                              maxOwed &&
                              maxOwed !== Infinity &&
                              numericValue > maxOwed
                            ) {
                              numericValue = maxOwed;
                              const decimals = getCurrencyDecimals(
                                group.currency
                              );
                              setAmountString(maxOwed.toFixed(decimals));
                            } else {
                              setAmountString(value);
                            }

                            setFormData({
                              ...formData,
                              amount: numericValue,
                            });
                          }}
                          className="text-2xl! font-bold! text-center"
                          autoFocus
                          data-testid="record-payment-amount-input"
                        />
                        {errors.amount && (
                          <p className="text-sm text-red-500 text-center mt-2">
                            {errors.amount}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Suggested/Max amount button - different for flexible vs regular mode */}
                    {flexibleOwnerMode &&
                    maxFlexibleAmount !== Infinity &&
                    maxFlexibleAmount > 0 ? (
                      <div className="flex justify-center">
                        <button
                          type="button"
                          onClick={() => {
                            const decimals = getCurrencyDecimals(
                              group.currency
                            );
                            setAmountString(
                              maxFlexibleAmount.toFixed(decimals)
                            );
                            setFormData({
                              ...formData,
                              amount: maxFlexibleAmount,
                            });
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:border-blue-300 dark:hover:border-blue-700 transition-all active:scale-[0.98]"
                        >
                          <Check className="h-4 w-4" />
                          <span>{t('settlements:labels.useFullAmount')}</span>
                          <span className="font-bold">
                            {formatCurrency(maxFlexibleAmount, group.currency)}
                          </span>
                        </button>
                      </div>
                    ) : (
                      selectedCredit &&
                      group.simplifyDebts && (
                        <div className="flex justify-center">
                          <button
                            type="button"
                            onClick={handleSuggestedAmount}
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:border-blue-300 dark:hover:border-blue-700 transition-all active:scale-[0.98]"
                          >
                            <Check className="h-4 w-4" />
                            <span>{t('settlements:labels.useFullAmount')}</span>
                            <span className="font-bold">
                              {formatCurrency(
                                selectedCredit.amount,
                                selectedCredit.currency || group.currency
                              )}
                            </span>
                          </button>
                        </div>
                      )
                    )}

                    {/* Payment Method */}
                    <div className="max-w-md mx-auto">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
                        {t('settlements:fields.paymentMethod')}
                      </label>
                      <div className="relative">
                        <select
                          id="method"
                          value={formData.method || 'cash'}
                          onChange={e =>
                            setFormData({ ...formData, method: e.target.value })
                          }
                          className="w-full pl-4 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 appearance-none text-center font-medium focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                          data-testid="record-payment-method-select"
                        >
                          {PAYMENT_METHODS.map(method => (
                            <option
                              key={method}
                              value={method}
                              data-testid={`method-option-${method}`}
                            >
                              {t(`settlements:paymentMethods.${method}`)}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
                      </div>
                    </div>

                    {/* Navigation buttons */}
                    <div className="flex justify-between gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleBack}
                        className="flex items-center gap-2"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        {t('common:actions.back')}
                      </Button>
                      <Button
                        type="button"
                        onClick={handleNext}
                        className="flex items-center gap-2 min-w-[120px]"
                        disabled={!formData.amount || formData.amount <= 0}
                      >
                        {t('common:actions.next')}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Confirm & Notes */}
                {currentStep === 3 && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        {t('settlements:labels.confirmRecordPayment')}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('settlements:labels.paymentWillBeAutoConfirmed')}
                      </p>
                    </div>

                    {/* Payment Summary */}
                    <div className="bg-linear-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4 sm:p-6 border border-blue-200 dark:border-blue-800">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center gap-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300 shrink-0">
                            {t('settlements:labels.paidBy')}
                          </span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {resolveUserName(
                              selectedMemberForDisplay?.userName,
                              t
                            )}
                          </span>
                        </div>
                        {/* Show who is receiving payment - for owner mode and flexible owner mode */}
                        {flexibleOwnerMode && flexibleCreditorInfo ? (
                          <div className="flex justify-between items-center gap-2">
                            <span className="text-sm text-gray-700 dark:text-gray-300 shrink-0">
                              {t('settlements:labels.paidTo')}
                            </span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                              {resolveUserName(
                                flexibleCreditorInfo.userName,
                                t
                              )}
                            </span>
                          </div>
                        ) : (
                          ownerMode &&
                          creditorInfo && (
                            <div className="flex justify-between items-center gap-2">
                              <span className="text-sm text-gray-700 dark:text-gray-300 shrink-0">
                                {t('settlements:labels.paidTo')}
                              </span>
                              <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                                {resolveUserName(creditorInfo.name, t)}
                              </span>
                            </div>
                          )
                        )}
                        <div className="flex justify-between items-center gap-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300 shrink-0">
                            {t('settlements:fields.amount')}
                          </span>
                          <span className="text-2xl font-bold text-blue-600 dark:text-blue-400 text-right leading-tight">
                            {formatCurrency(
                              formData.amount || 0,
                              group.currency
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between items-center gap-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300 shrink-0">
                            {t('settlements:fields.paymentMethod')}
                          </span>
                          <span className="font-medium text-gray-900 dark:text-gray-100 text-right">
                            {t(
                              `settlements:paymentMethods.${formData.method || 'cash'}`
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Auto-complete notice - different message for owner mode */}
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
                      <p className="text-sm text-green-700 dark:text-green-400">
                        {ownerMode || flexibleOwnerMode
                          ? t('settlements:labels.ownerSettlementNotice')
                          : t('settlements:labels.autoCompleteNotice', {
                              name: resolveUserName(
                                selectedMemberForDisplay?.userName,
                                t
                              ),
                            })}
                      </p>
                    </div>

                    {/* Notes (Optional) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('settlements:fields.notes')}{' '}
                        <span className="text-gray-500">
                          ({t('settlements:fields.optional')})
                        </span>
                      </label>
                      <textarea
                        id="notes"
                        placeholder={t(
                          'settlements:placeholders.additionalNotes'
                        )}
                        value={formData.notes || ''}
                        onChange={e =>
                          setFormData({ ...formData, notes: e.target.value })
                        }
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 resize-none"
                        data-testid="record-payment-notes-input"
                      />
                    </div>

                    {/* Navigation buttons */}
                    <div className="flex justify-between gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleBack}
                        disabled={isSubmitting}
                        className="flex items-center gap-2"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        {t('common:actions.back')}
                      </Button>
                      <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex items-center gap-2 min-w-[160px] bg-blue-600 hover:bg-blue-700"
                        data-testid="record-payment-submit"
                      >
                        {isSubmitting ? (
                          <>
                            <LoadingSpinner size="sm" />
                            {t('common:actions.creating')}
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4" />
                            {ownerMode || flexibleOwnerMode
                              ? t('settlements:actions.recordPaymentGeneric')
                              : t('settlements:actions.recordPayment')}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Portal>
  );
}
